import React, { useState, useEffect, useContext, useRef } from 'react';
import { db } from '../../../firebase/config';
import { collection, query, orderBy, getDocs, where, Timestamp } from 'firebase/firestore';
import { TranslationHelper } from '../../../common/creators/helpers/translationHelper';
import styles from './cashierClosesList.module.css';
import { appContext } from 'common/contexts/appContext';
import { SearchTwoTone } from 'common/icons';
import { printComponent } from 'react-print-tool';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

type CashierClosesListProps = {
  translation: TranslationHelper;
};

type MonthOption = {
  value: string;
  label: string;
  startDate: number;
  endDate: number;
};

type CashierClose = {
  id: string;
  date: number;
  summary: {
    totalSales: number;
    cashSales: number;
    cardSales: number;
    orderCount: number;
  };
  countedCash: number;
  difference: number;
  userId: string | null;
  createdBy?: string;
  parentUserId?: string;
  ownerId?: string;
};

const CashierClosesList: React.FC<CashierClosesListProps> = ({ translation }) => {
  const { t, formatDate } = translation;
  const [cashierCloses, setCashierCloses] = useState<CashierClose[]>([]);
  const [filteredCloses, setFilteredCloses] = useState<CashierClose[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { state } = useContext(appContext);
  const [monthOptions, setMonthOptions] = useState<MonthOption[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<MonthOption | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Función para obtener el nombre del usuario por su ID
  const getUserName = (userId: string | null | undefined) => {
    if (!userId) return 'Usuario desconocido';
    const user = state.users.find(u => u.id === userId);
    return user ? user.displayName : 'Usuario desconocido';
  };

  useEffect(() => {
    fetchCashierCloses();
  }, []);
  
  // Generar opciones de mes al montar el componente
  useEffect(() => {
    const generateMonthOptions = () => {
      const options: MonthOption[] = [];
      const currentDate = new Date();
      
      // Generar opciones para los últimos 12 meses
      for (let i = 0; i < 12; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthName = format(date, 'MMMM yyyy', { locale: es });
        const capitalizedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
        
        options.push({
          value: `${date.getFullYear()}-${date.getMonth() + 1}`,
          label: capitalizedMonthName,
          startDate: startOfMonth,
          endDate: endOfMonth
        });
      }
      
      setMonthOptions(options);
      
      // Seleccionar el mes actual por defecto
      if (options.length > 0) {
        setSelectedMonth(options[0]);
      }
    };
    
    generateMonthOptions();
  }, []);
  
  // Filtrar cierres por mes cuando cambia el mes seleccionado o los cierres
  useEffect(() => {
    if (selectedMonth && cashierCloses.length > 0) {
      const filtered = cashierCloses.filter(close => {
        return close.date >= selectedMonth.startDate && close.date <= selectedMonth.endDate;
      });
      setFilteredCloses(filtered);
    } else {
      setFilteredCloses(cashierCloses);
    }
  }, [selectedMonth, cashierCloses]);

  const fetchCashierCloses = async () => {
    setLoading(true);
    setError(null);
    try {
      const cashierClosesCollection = collection(db, 'cashierCloses');
      const q = query(cashierClosesCollection, where('parentUserId', '==', state.parentId), orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const closes: CashierClose[] = [];
      querySnapshot.forEach((doc) => {
        closes.push({ id: doc.id, ...doc.data() } as CashierClose);
      });
      
      setCashierCloses(closes);
    } catch (err) {
      console.error('Error fetching cashier closes:', err);
      setError(t('admin.cashierCloses.fetchError', 'Error al cargar los cierres de caja'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    const selectedOption = monthOptions.find(option => option.value === selectedValue) || null;
    setSelectedMonth(selectedOption);
  };
  
  const handlePrint = () => {
    if (printRef.current) {
      // Crear el componente a imprimir
      const PrintableContent = () => (
        <div style={{ padding: '20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h1>{t('admin.cashierCloses.printTitle', 'Informe de Cierres de Caja')}</h1>
            {selectedMonth && <p>{selectedMonth.label}</p>}
          </div>
          
          {/* Tabla personalizada para imprimir en lugar de usar dangerouslySetInnerHTML */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashierCloses.date', 'Fecha')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashierCloses.user', 'Usuario')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashierCloses.totalSales', 'Ventas Totales')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashierCloses.cashSales', 'Ventas en Efectivo')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashierCloses.cardSales', 'Ventas con Tarjeta')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashierCloses.orderCount', 'Cantidad de Órdenes')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashierCloses.countedCash', 'Efectivo Contado')}
                </th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: '#f2f2f2' }}>
                  {t('admin.cashierCloses.difference', 'Diferencia')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCloses.length > 0 ? (
                filteredCloses.map((close) => (
                  <tr key={close.id}>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {formatTimestamp(close.date)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {getUserName(close.ownerId)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {formatCurrency(close.summary.totalSales)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {formatCurrency(close.summary.cashSales)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {formatCurrency(close.summary.cardSales)}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {close.summary.orderCount}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                      {formatCurrency(close.countedCash)}
                    </td>
                    <td style={{ 
                      border: '1px solid #ddd', 
                      padding: '8px', 
                      textAlign: 'left',
                      color: close.difference < 0 ? 'red' : close.difference > 0 ? 'green' : 'inherit'
                    }}>
                      {formatCurrency(close.difference)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ border: '1px solid #ddd', padding: '20px', textAlign: 'center' }}>
                    {t('admin.cashierCloses.noResults', 'No hay cierres de caja para el período seleccionado')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
      
      // Utilizar printComponent para imprimir
      printComponent(<PrintableContent />);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return formatDate(date, 'dd/MM/yyyy HH:mm'); // Format: 20/04/2025 11:22
  };

  if (loading) {
    return <div className={styles.loading}>{t('common.loading', 'Cargando...')}</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (cashierCloses.length === 0 && !loading) {
    return (
      <div className={styles.empty}>
        {t('admin.cashierCloses.empty', 'No hay cierres de caja registrados')}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('admin.cashierCloses.title', 'Historial de Cierres de Caja')}</h1>
        
        <div className={styles.actions}>
          {/* Filtro por mes */}
          <div className={styles.filterContainer}>
            <SearchTwoTone className={styles.filterIcon} />
            <select
              className={styles.monthFilter}
              value={selectedMonth?.value || ''}
              onChange={handleMonthChange}
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Botón de imprimir */}
          <button className="btn btn-primary" onClick={handlePrint}>
            {t('common.print')}
          </button>
        </div>
      </div>
      
      <div className={styles.tableContainer} ref={printRef}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('admin.cashierCloses.date', 'Fecha')}</th>
              <th>{t('admin.cashierCloses.user', 'Usuario')}</th>
              <th>{t('admin.cashierCloses.totalSales', 'Ventas Totales')}</th>
              <th>{t('admin.cashierCloses.cashSales', 'Ventas en Efectivo')}</th>
              <th>{t('admin.cashierCloses.cardSales', 'Ventas con Tarjeta')}</th>
              <th>{t('admin.cashierCloses.orderCount', 'Cantidad de Órdenes')}</th>
              <th>{t('admin.cashierCloses.countedCash', 'Efectivo Contado')}</th>
              <th>{t('admin.cashierCloses.difference', 'Diferencia')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredCloses.length > 0 ? (
              filteredCloses.map((close) => (
                <tr key={close.id}>
                  <td>{formatTimestamp(close.date)}</td>
                  <td>{getUserName(close.ownerId)}</td>
                  <td>{formatCurrency(close.summary.totalSales)}</td>
                  <td>{formatCurrency(close.summary.cashSales)}</td>
                  <td>{formatCurrency(close.summary.cardSales)}</td>
                  <td>{close.summary.orderCount}</td>
                  <td>{formatCurrency(close.countedCash)}</td>
                  <td className={`${styles.difference} ${close.difference < 0 ? styles.negative : close.difference > 0 ? styles.positive : ''}`}>
                    {formatCurrency(close.difference)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className={styles.noResults}>
                  {t('admin.cashierCloses.noResults', 'No hay cierres de caja para el período seleccionado')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CashierClosesList;
